import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useRegistration } from './RegistrationContext';
import StepIndicator from './StepIndicator';
import FormInput from '../common/FormInput';
import MainLayout from '../common/MainLayout';

function MembershipSelection() {
  const navigate = useNavigate();
  const { registrationData, updateRegistrationData } = useRegistration();
  const [touched, setTouched] = useState({});
  const [errors] = useState({});

  const handleBlur = e => {
    const { name } = e.target;
    setTouched(prev => ({
      ...prev,
      [name]: true,
    }));
  };

  const membershipTypes = [
    {
      id: 'individual',
      name: 'Individual Membership',
      price: '£70',
      description: 'Perfect for single members',
      features: ['Full club access', 'Event participation', 'Monthly newsletter'],
    },
    {
      id: 'joint',
      name: 'Joint Membership',
      price: '£85',
      description: 'For two people',
      features: ['For two adults', 'Shared club access', 'Event participation for both'],
    },
    {
      id: 'youth',
      name: 'Youth Membership',
      price: '£15',
      description: 'For members under 21',
      features: ['Full club access', 'Event participation', 'Age verification required'],
    },
  ];

  const handleSubmit = e => {
    e.preventDefault();
    navigate('/membership/payment');
  };

  return (
    <MainLayout>
      <StepIndicator currentStep={3} />

      <h2 className="text-2xl font-bold mb-8 text-hdlcc-textPrimary tracking-tight">
        Select Membership Type
      </h2>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="space-y-5">
          {membershipTypes.map(type => (
            <button
              key={type.id}
              type="button"
              className={`w-full p-6 rounded-xl cursor-pointer border-2 transition-all duration-200 text-left shadow-elevation-1 ${
                registrationData.membershipType === type.id
                  ? 'border-hdlcc-primary bg-gradient-to-br from-hdlcc-mediumGray to-hdlcc-gray shadow-glow-primary'
                  : 'border-hdlcc-lightGray/20 bg-hdlcc-mediumGray hover:border-hdlcc-primary/50 hover:shadow-elevation-2'
              }`}
              onClick={() => updateRegistrationData({ membershipType: type.id })}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  updateRegistrationData({ membershipType: type.id });
                }
              }}
              aria-label={`Select ${type.name}`}
            >
              <div className="flex items-start space-x-4">
                <input
                  type="radio"
                  name="membershipType"
                  value={type.id}
                  checked={registrationData.membershipType === type.id}
                  onChange={() => updateRegistrationData({ membershipType: type.id })}
                  className="mt-1 text-hdlcc-primary focus:ring-hdlcc-primary"
                />
                <div className="flex-grow">
                  <div className="flex justify-between items-start mb-2">
                    <h3 className="text-xl font-bold text-hdlcc-textPrimary">{type.name}</h3>
                    <span className="text-2xl font-bold text-hdlcc-primary">{type.price}</span>
                  </div>
                  <p className="text-hdlcc-textSecondary mb-4">{type.description}</p>
                  <ul className="space-y-2">
                    {type.features.map((feature, index) => (
                      <li key={index} className="text-hdlcc-textSecondary flex items-center">
                        <svg
                          className="w-5 h-5 text-hdlcc-success mr-2 flex-shrink-0"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </button>
          ))}
        </div>

        {registrationData.membershipType === 'joint' && (
          <div className="mt-6 bg-hdlcc-mediumGray border border-hdlcc-lightGray/20 rounded-xl p-6 shadow-elevation-1">
            <h3 className="text-lg font-bold text-hdlcc-textPrimary mb-6">Second Member Details</h3>
            <div className="grid grid-cols-2 gap-6">
              <FormInput
                label="First Name"
                name="secondMemberFirstName"
                value={registrationData.secondMemberFirstName || ''}
                onChange={e => updateRegistrationData({ secondMemberFirstName: e.target.value })}
                onBlur={handleBlur}
                error={errors.secondMemberFirstName}
                touched={touched.secondMemberFirstName}
              />
              <FormInput
                label="Last Name"
                name="secondMemberLastName"
                value={registrationData.secondMemberLastName || ''}
                onChange={e => updateRegistrationData({ secondMemberLastName: e.target.value })}
                onBlur={handleBlur}
                error={errors.secondMemberLastName}
                touched={touched.secondMemberLastName}
              />
            </div>
          </div>
        )}

        <div className="mt-8 flex justify-between">
          <button
            type="button"
            onClick={() => navigate('/membership/address')}
            className="bg-hdlcc-mediumGray border border-hdlcc-lightGray/20 text-white px-8 py-3 rounded-xl hover:bg-hdlcc-gray transition-all duration-200 font-semibold shadow-elevation-1"
          >
            Previous
          </button>
          <button
            type="submit"
            className="bg-gradient-to-r from-hdlcc-primary to-hdlcc-primaryLight text-white px-8 py-3 rounded-xl hover:shadow-glow-primary transition-all duration-200 font-semibold shadow-elevation-1"
          >
            Next: Payment Details
          </button>
        </div>
      </form>
    </MainLayout>
  );
}

export default MembershipSelection;
